import { Link } from 'react-router-dom';
import LegalShell from '../components/LegalShell';

const Dpa = () => (
  <LegalShell title="Data Processing" accent="Agreement">
      <section className="legal-section">
        <h2 className="legal-h2">1. Parties and scope</h2>
        <p className="legal-p">
          1.1 This Data Processing Agreement ("DPA") is concluded between the customer named in
          the account or the individual agreement ("Customer", the controller) and Qombra B.V.,
          Valckenierstraat 65, 1018 WV Amsterdam, the Netherlands ("Qombra", the processor). It
          forms part of the <Link className="legal-link" to="/terms">Terms of Service</Link> and
          takes effect when Customer first makes personal data available to the Services.
        </p>
        <p className="legal-p">
          1.2 This DPA applies to all personal data that Customer uploads to or processes through
          the Qombra platform, the QBrain API and SDK or QAgent, including personal data in
          tables, API payloads, prompts, connected data sources and resulting predictions
          ("Customer Personal Data"). It implements Art. 28(3) GDPR. Personal data that Qombra
          processes as controller (account, billing, support) is covered by the{' '}
          <Link className="legal-link" to="/privacy">Privacy Policy</Link>, not by this DPA.
        </p>
        <p className="legal-p">
          1.3 Subject matter, duration, nature and purpose of the processing and the categories of
          data subjects and data are described in Annex 1.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">2. Instructions</h2>
        <p className="legal-p">
          2.1 Qombra processes Customer Personal Data only on documented instructions from
          Customer, including with regard to transfers to third countries, unless required to do
          so by Union or Member State law; in that case Qombra informs Customer of the legal
          requirement before processing, unless the law prohibits this on important grounds of
          public interest.
        </p>
        <p className="legal-p">
          2.2 The Terms, this DPA, the configuration of the Services and the actions Customer
          takes in the platform, through the API or in instructions to QAgent constitute
          Customer's instructions. Further instructions must be given in writing.
        </p>
        <p className="legal-p">
          2.3 Qombra informs Customer without delay if, in its opinion, an instruction infringes
          the GDPR or other data protection law. Qombra may suspend the execution of such an
          instruction until Customer confirms or changes it.
        </p>
        <p className="legal-p">
          2.4 Qombra does not use Customer Personal Data for its own purposes, in particular not to
          train or improve models made available to other customers.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">3. Confidentiality</h2>
        <p className="legal-p">
          Qombra ensures that persons authorised to process Customer Personal Data have committed
          themselves to confidentiality or are under an appropriate statutory obligation of
          confidentiality, and that access is limited to what is necessary to provide, secure
          and support the Services.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">4. Security</h2>
        <p className="legal-p">
          Qombra implements the technical and organisational measures described in Annex 2 and
          keeps them appropriate to the risk in accordance with Art. 32 GDPR. Qombra may update the
          measures provided the level of protection is not reduced.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">5. Sub-processors</h2>
        <p className="legal-p">
          5.1 Customer gives Qombra general authorisation to engage the sub-processors listed in
          Annex 3. Qombra imposes on each sub-processor, by written contract, data protection
          obligations that are at least as protective as those in this DPA, and remains fully
          liable to Customer for the performance of the sub-processor's obligations.
        </p>
        <p className="legal-p">
          5.2 Qombra informs Customer by email or in the platform at least 14 days before adding
          or replacing a sub-processor. Customer may object in writing on reasonable data
          protection grounds within that period. If the parties cannot resolve the objection,
          Customer may terminate the affected Services with effect from the date the change takes
          effect; fees already paid for the remaining term are refunded pro rata.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">6. Assistance to Customer</h2>
        <p className="legal-p">
          6.1 Taking into account the nature of the processing, Qombra assists Customer with
          appropriate technical and organisational measures in fulfilling its obligation to
          respond to requests of data subjects. Qombra forwards any request it receives directly
          from a data subject concerning Customer Personal Data to Customer within 5 business
          days and does not respond to it itself, except to refer the data subject to Customer.
        </p>
        <p className="legal-p">
          6.2 Customer can access, correct, export and delete Customer Personal Data itself
          through the platform, the API and the SDK. Where that is not sufficient, Qombra
          assists on request against reasonable compensation.
        </p>
        <p className="legal-p">
          6.3 Qombra assists Customer in ensuring compliance with Art. 32 to 36 GDPR (security,
          breach notification, data protection impact assessments and prior consultation),
          taking into account the nature of the processing and the information available to
          Qombra. On request, Qombra provides a description of the processing, the security
          measures and the model types used, and the explanation features available in the
          Services.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">7. Personal data breaches</h2>
        <p className="legal-p">
          Qombra notifies Customer without undue delay, and no later than 48 hours after becoming
          aware of it, of a personal data breach affecting Customer Personal Data. The
          notification describes, as far as known, the nature of the breach, the categories and
          approximate number of data subjects and records concerned, the likely consequences, the
          measures taken or proposed, and a contact point. Information that is not yet available
          is provided as it becomes available. Qombra cooperates with Customer in investigating
          and containing the breach.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">8. Deletion and return</h2>
        <p className="legal-p">
          8.1 Customer may delete Customer Personal Data at any time through the platform, the
          API or the SDK, or by deleting its account. Deletion through these functions is
          permanent.
        </p>
        <p className="legal-p">
          8.2 At the end of the contract, Customer can export Customer Personal Data for 30 days.
          Thereafter Qombra deletes all Customer Personal Data, including per-customer models and
          stored results, within 30 days, unless Union or Member State law requires storage.
          Copies in backups are overwritten within a further 35 days and are not restored except
          for disaster recovery. If Qombra restores a backup that contains Customer Personal Data
          already deleted, Qombra deletes that data again without undue delay and at the latest
          within 7 days after the restore.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">9. Audits</h2>
        <p className="legal-p">
          Qombra makes available to Customer all information necessary to demonstrate compliance
          with Art. 28 GDPR, first by answering written questions and providing documentation.
          Where that is reasonably insufficient, Customer or an independent auditor bound by
          confidentiality may audit the processing once per calendar year, or additionally after a
          personal data breach or at the request of a supervisory authority, with at least 30
          days' written notice, during business hours, without disrupting operations, and at
          Customer's cost. Audits of sub-processors are governed by the sub-processors' own audit
          arrangements, which Qombra makes available where it can.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">10. Location of processing and transfers</h2>
        <p className="legal-p">
          Customer Personal Data is stored and processed in the European Union (Annex 3). Qombra
          does not transfer Customer Personal Data to a third country except on Customer's
          documented instruction or as described in Annex 3, and only with an appropriate
          safeguard under Chapter V GDPR.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">11. Special categories of data</h2>
        <p className="legal-p">
          Special categories of personal data (Art. 9 GDPR) and personal data relating to criminal
          convictions and offences (Art. 10 GDPR) may be processed only where the parties have
          agreed this in writing in advance, specifying the categories, the purpose and any
          additional measures.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">12. Liability, term and general</h2>
        <p className="legal-p">
          12.1 The limitations of liability in the Terms apply to this DPA, except where
          mandatory law provides otherwise. Each party is liable to data subjects in accordance
          with Art. 82 GDPR.
        </p>
        <p className="legal-p">
          12.2 This DPA lasts as long as Qombra processes Customer Personal Data. Clauses 3, 8 and
          12 survive its end. In case of conflict between this DPA and the Terms, this DPA
          prevails with respect to the processing of Customer Personal Data. Dutch law applies
          and the competent court in Amsterdam has exclusive jurisdiction.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">Annex 1: Description of the processing</h2>
        <table className="legal-table">
          <tbody>
            <tr><td>Subject matter</td><td>Hosting, preparation, analysis and predictive modelling of tabular data uploaded by Customer; AI-assisted data preparation and question answering (QAgent); storage and delivery of resulting models and predictions.</td></tr>
            <tr><td>Duration</td><td>The term of the contract, plus the deletion periods in clause 8.</td></tr>
            <tr><td>Nature</td><td>Storage, structuring, transformation, statistical analysis, model training and inference, forecasting, explanation of predictions, retrieval and deletion. Processing is automated; Qombra staff access Customer Personal Data only for support, security and troubleshooting.</td></tr>
            <tr><td>Purpose</td><td>Providing the Services to Customer as described in the Terms and the documentation.</td></tr>
            <tr><td>Data subjects</td><td>Determined by Customer. Typically Customer's customers, prospects, users, suppliers or employees, and other persons represented as rows in Customer's tables.</td></tr>
            <tr><td>Categories of data</td><td>Determined by Customer. Typically identifiers, contact and account details, transactional, behavioural, financial and operational attributes, and derived scores. Special categories only under clause 11.</td></tr>
          </tbody>
        </table>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">Annex 2: Technical and organisational measures</h2>
        <ul className="legal-ul">
          <li><strong>Encryption:</strong> all data encrypted at rest (AES-256) and in transit (TLS); insecure transport to storage is rejected.</li>
          <li><strong>Location:</strong> storage and processing in EU data centres only; the language-model provider is configured to EU endpoints and does not retain or train on prompts.</li>
          <li><strong>Access control:</strong> authentication for every request; API keys and session tokens stored only as hashes; session tokens expire after 12 hours; per-user and per-organisation authorisation; staff access restricted to named personnel on a need-to-know basis.</li>
          <li><strong>Isolation:</strong> customer data separated by account and organisation; transient GPU processing uses dedicated, access-scoped storage that is emptied after each job and expires automatically within 24 hours.</li>
          <li><strong>Availability:</strong> managed cloud infrastructure with automated backups; backups encrypted and retained for at most 35 days.</li>
          <li><strong>Integrity and logging:</strong> security-relevant events logged for at most 30 days; rate limiting and abuse detection; no logging of table contents or predictions.</li>
          <li><strong>Deletion:</strong> self-service deletion of datasets, models, results and accounts; staged copies for GPU jobs deleted immediately after the job.</li>
          <li><strong>Organisation:</strong> confidentiality obligations for all personnel; least-privilege cloud credentials; change management and dependency updates; incident response procedure with the notification duty in clause 7.</li>
        </ul>
      </section>

      <section className="legal-section" id="sub-processors">
        <h2 className="legal-h2">Annex 3: Sub-processors</h2>
        <table className="legal-table">
          <thead>
            <tr><th>Sub-processor</th><th>Purpose</th><th>Location of processing</th><th>Transfer safeguard</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Amazon Web Services EMEA SARL, 38 avenue John F. Kennedy, L-1855 Luxembourg</td>
              <td>Hosting, database and object storage (AWS Frankfurt); transactional email (Amazon SES); language-model inference for QAgent (Amazon Bedrock, Anthropic Claude models, EU region; prompts and outputs are not stored or used for training by AWS or the model vendor).</td>
              <td>EU (Germany, eu-central-1; EU cross-region inference for Bedrock)</td>
              <td>Not required (EU entity, EU processing). AWS Data Processing Addendum.</td>
            </tr>
            <tr>
              <td>Runpod, Inc., Mount Laurel, New Jersey, United States</td>
              <td>GPU compute for model training and prediction jobs.</td>
              <td>Dataset contents: EU data centres only. Job coordination metadata (job identifiers, status, timing; no dataset contents or column names): United States.</td>
              <td>Standard Contractual Clauses (Art. 46(2)(c) GDPR) in the data processing agreement with Runpod.</td>
            </tr>
          </tbody>
        </table>
        <p className="legal-p">
          Google Analytics is used on the public website only and never processes Customer
          Personal Data; see the <Link className="legal-link" to="/privacy">Privacy Policy</Link>.
        </p>
      </section>
  </LegalShell>
);

export default Dpa;
